import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts';
import { useModels } from './useModels';

export type NewPromptActionLabel = string;

export interface PromptFormProps {
  onSubmit: (data: { title: string; description: string; full_text: string, systemprompt: string; user: string; is_public?: boolean; model_type: string; }) => Promise<void> | void;
  onEnhancePrompt?: (prompt: string) => Promise<string>;
}

export const useNewPromptData = ({
  onSubmit,
  onEnhancePrompt
}: PromptFormProps) => {
  // Field values
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fullText, setFullText] = useState('');
  const [systemprompt, setSystemprompt] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [selectedModel, setSelectedModel] = useState('');
  const [errors, setErrors] = useState<{ title?: string; description?: string; full_text?: string; }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // System prompt UI state
  const [isSystemPromptExpanded, setIsSystemPromptExpanded] = useState(false);
  
  // Enhancement state for both fields
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancementError, setEnhancementError] = useState<string | null>(null);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [hasBeenEnhanced, setHasBeenEnhanced] = useState(false);
  
  // Original values for both fields
  const [originalSystemPrompt, setOriginalSystemPrompt] = useState<string>('');
  const [originalFullText, setOriginalFullText] = useState<string>('');
  
  // Enhanced values for both fields  
  const [, setEnhancedSystemPrompt] = useState<string>('');
  const [enhancedFullText, setEnhancedFullText] = useState<string>('');
  
  const { user } = useAuth();
  const { models } = useModels();
  const { t } = useLanguage();

  // Set default model when models are loaded
  useEffect(() => {
    if (models.length > 0 && !selectedModel) {
      // Default to Claude 3 Haiku if available, otherwise first model
      const defaultModel = models.find(m => m.name === 'claude-3-haiku') || models[0];
      setSelectedModel(defaultModel.name);
    }
  }, [models, selectedModel]);

  // Derived state
  const shouldShowGenerateInitialButton = !systemprompt.trim() && !fullText.trim();
  const isGenerateEnabled = title.trim() !== '' && description.trim() !== '';
  const actionButtonLabel: NewPromptActionLabel = shouldShowGenerateInitialButton ? t('generateInitialPrompt') : t('enhancePrompt');
  const actionButtonDisabled = shouldShowGenerateInitialButton ? !isGenerateEnabled : false;
  const actionButtonTooltip = shouldShowGenerateInitialButton && !isGenerateEnabled 
    ? "Fill in the Title and Short Description fields to generate an initial prompt." 
    : undefined;

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!description.trim()) newErrors.description = 'Description is required';
    if (!fullText.trim()) newErrors.full_text = 'Full text is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate initial prompt from title and description
  const generateInitialFromTitleAndDescription = async () => {
    if (!title.trim() || !description.trim()) return;
    if (!onEnhancePrompt) return;

    // Build a seed instruction that the enhancer API will transform into a full usable prompt.
    const seed = `TITLE: ${title.trim()}\nDESCRIPTION: ${description.trim()}\n\nTASK: Craft a high-quality, production-ready initial AI prompt the user can employ directly.\nREQUIREMENTS:\n- Reflect the title & description intent.\n- Include clear objective, constraints, style/tone, and desired output structure if applicable.\n- Use markdown for headings & lists when helpful.\nOUTPUT: Provide ONLY the finalized prompt (no commentary).`;

    // System prompt generation disabled for now - always set to empty
    setSystemprompt('');

    // Reset states & originals
    setIsEnhancing(true);
    setEnhancementError(null);
    setIsEnhanced(false);
    setHasBeenEnhanced(false);
    setOriginalFullText('');
    setFullText('');

    try {
      const generated = await onEnhancePrompt(seed);
      setFullText(generated);
      // Not marking as enhanced; this is baseline generation
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate initial prompt';
      setEnhancementError(msg);
    } finally {
      setIsEnhancing(false);
    }
  };

  // Enhance both system prompt and prompt
  const enhanceBoth = () => {
    // Store originals
    setOriginalSystemPrompt(systemprompt);
    setOriginalFullText(fullText);

    // Generate enhanced system prompt
    const baseSystemPrompt = systemprompt.trim() || "You are a helpful AI assistant.";
    const enhancedSystemPromptText = `[ENHANCED] Based on the role: '${baseSystemPrompt}', your primary goal is to provide clear, concise, and accurate information. You must avoid speculation and adhere strictly to the user's instructions.`;

    // Generate enhanced prompt  
    const enhancedPromptText = `[ENHANCED]

**User's Original Goal:**
${fullText}

**Optimized Instructions:**
1. Analyze the user's core request.
2. Structure the response logically.
3. Provide examples where applicable.`;

    // Set enhanced content - only set system prompt if expanded
    setEnhancedSystemPrompt(enhancedSystemPromptText);
    setEnhancedFullText(enhancedPromptText);
    
    if (isSystemPromptExpanded) {
      setSystemprompt(enhancedSystemPromptText);
    }
    setFullText(enhancedPromptText);
    
    // Set enhanced state
    setIsEnhanced(true);
    setHasBeenEnhanced(true);
    setEnhancementError(null);
  };

  // Revert to original content
  const revertBoth = () => {
    if (originalSystemPrompt === '' && originalFullText === '') {
      // If originals were empty, clear both and collapse
      setSystemprompt('');
      setFullText('');
      setIsSystemPromptExpanded(false);
    } else {
      // Restore originals and keep expanded if system prompt had content
      setSystemprompt(originalSystemPrompt);
      setFullText(originalFullText);
      if (originalSystemPrompt) {
        setIsSystemPromptExpanded(true);
      }
    }
    
    setIsEnhanced(false);
    setEnhancementError(null);
  };

  // Clear all fields
  const clearAll = () => {
    setTitle('');
    setDescription('');
    setSystemprompt('');
    setFullText('');
    setIsSystemPromptExpanded(false);
    setIsEnhanced(false);
    setHasBeenEnhanced(false);
    setEnhancementError(null);
    setErrors({});
    setOriginalSystemPrompt('');
    setOriginalFullText('');
    setEnhancedSystemPrompt('');
    setEnhancedFullText('');
    // Don't clear selectedModel - keep the current selection
  };

  // Legacy enhance prompt function for compatibility
  const handleEnhancePrompt = async () => {
    if (!fullText.trim() || !onEnhancePrompt) return;

    setIsEnhancing(true);
    setEnhancementError(null);

    try {
      // Store original text before enhancement
      setOriginalFullText(fullText.trim());
      const enhanced = await onEnhancePrompt(fullText);
      setEnhancedFullText(enhanced);
      setFullText(enhanced);
      setIsEnhanced(true);
      setHasBeenEnhanced(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to enhance prompt';
      setEnhancementError(errorMessage);
    } finally {
      setIsEnhancing(false);
    }
  };

  // Legacy handlers for compatibility
  const handleRevertToOriginal = () => {
    if (originalFullText) {
      setFullText(originalFullText);
      setIsEnhanced(false);
      setEnhancementError(null);
    }
  };

  const handleToggleToEnhanced = () => {
    if (hasBeenEnhanced && enhancedFullText) {
      setFullText(enhancedFullText);
      setIsEnhanced(true);
      setEnhancementError(null);
    }
  };

  const handleResetEnhancementState = () => {
    setIsEnhanced(false);
    setHasBeenEnhanced(false);
    setEnhancementError(null);
    setOriginalSystemPrompt('');
    setOriginalFullText('');
    setEnhancedSystemPrompt('');
    setEnhancedFullText('');
  };

  const getUserId = (): string => {
    if (!user?.id) throw new Error('User not found');
    return user.id;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    if (!validate()) return;
    setLoading(true);
    try {
      await onSubmit({ 
        title, 
        description, 
        full_text: fullText, 
        systemprompt, 
        user: getUserId(),
        is_public: isPublic,
        model_type: selectedModel
      });
      // Don't clear fields here - let the parent component handle navigation
    } catch (err: unknown) {
      if (err instanceof Error) {
        setApiError(err.message);
      } else {
        setApiError('Failed to create prompt');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    // Field values
    title,
    description,
    fullText,
    systemprompt,
    isPublic,
    selectedModel,

    // UI/derived state  
    isSystemPromptExpanded,
    isEnhanced,
    hasBeenEnhanced,
    
    // Async state
    loading,
    isEnhancing,
    
    // Validation/errors
    errors,
    apiError,
    enhancementError,
    
    // Contextual primary action button
    actionButtonLabel,
    actionButtonDisabled,
    actionButtonTooltip,
    
    // Setters
    setTitle,
    setDescription,
    setFullText,
    setSystemprompt,
    setIsPublic,
    setSelectedModel,
    setIsSystemPromptExpanded,
    
    // New Actions
    generateInitialFromTitleAndDescription,
    enhanceBoth,
    revertBoth,
    clearAll,
    
    // Legacy actions for compatibility
    handleEnhancePrompt,
    handleRevertToOriginal,
    handleToggleToEnhanced,
    resetForm: handleResetEnhancementState,
    handleSubmit,
    getUserId,
    validate,
  };
};
